import type { Asset, AssetVariant } from './demo';

/**
 * Variant selection, shared by player and editor so previews match production.
 *
 * The player's budget is about media, not JS (PRODUCT-PLAN §5.4): the JS is 60KB
 * but a 25-step demo is several MB of frames. Picking the smallest variant that
 * still looks sharp is the single biggest lever on time-to-first-interactive-frame.
 */

/**
 * Beyond this the extra pixels are invisible and only cost bandwidth.
 *
 * 3, not the 2.5 it was (2026-09-29). The player's step pictures never
 * honoured the cap: a <picture> with `w` descriptors is chosen by the browser
 * at the device's real DPR, so on a 3x phone the cap only made prefetch and
 * Studio ask for a smaller file than the one shown. Applying it for real
 * (shrinking `sizes`) would soften exactly the close-ups that look worst. It
 * would save little: with native / 2/3 / 1/3 variants the cap changes the pick
 * only when a variant lies between 2.5x and 3x the drawn width — drawn widths
 * of 569–683 or 853–1024 CSS px for a 2560 capture — and a phone's story
 * shots and legibility zoom draw a 2560 capture 1400+ CSS px wide, past the
 * native file anyway, while a 390 px overview wants the 2/3 file at both
 * 2.5x and 3x. Where it would bite, the next file up costs ~1.4x the bytes
 * (text-dense 2560x1600 test frame, AVIF q75 4:4:4: 853 → 92 KB, 1707 →
 * 251 KB, 2560 → 360 KB) for 20% more pixels across on a screen that shows
 * them. At 3 the cap matches what browsers already download for 3x phones
 * and trims only the rare 3.5–4x Android.
 */
const MAX_EFFECTIVE_DPR = 3;

/**
 * Smallest variant at least as wide as the rendered size. Falls back to the
 * widest available when the target exceeds everything we encoded.
 */
export function pickVariant(asset: Asset, cssWidth: number, dpr = 1): AssetVariant {
  const target = cssWidth * Math.min(dpr, MAX_EFFECTIVE_DPR);
  const sorted = [...asset.variants].sort((a, b) => a.w - b.w);
  return sorted.find((v) => v.w >= target) ?? sorted[sorted.length - 1]!;
}

/** `srcset` string for one codec, or undefined when no variant carries it. */
export function srcsetFor(asset: Asset, codec: 'avif' | 'webp' | 'jpg'): string | undefined {
  const parts = asset.variants
    .filter((v) => v[codec])
    .sort((a, b) => a.w - b.w)
    .map((v) => `${v[codec]} ${v.w}w`);
  return parts.length ? parts.join(', ') : undefined;
}

/** Aspect ratio, used to reserve layout space before the image decodes (CLS = 0). */
export function aspectOf(asset: Pick<Asset, 'w' | 'h'>): number {
  return asset.w / asset.h;
}

/**
 * How a variant URL is written into a document: root-relative, `/a/<storage key>`.
 *
 * Never an absolute CDN URL. Published documents are immutable and live for the
 * life of the product (CLAUDE.md), so a host baked into them could never change
 * — switching CDN would break every embed ever made. The player resolves these
 * against the origin it fetched the document from (or its `asset-base`
 * attribute when the document is inline), and every Mado origin serves `/a/*`.
 */
export const ASSET_PATH_PREFIX = '/a/';

export function assetPath(storageKey: string): string {
  return ASSET_PATH_PREFIX + storageKey.replace(/^\/+/, '');
}
